import type {
  ElementFileUploadEvent,
  QuestionFileModelData,
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyImageAnswerValue,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type { SurveyElementFormHandler } from './survey-element-form'
import { acceptedTypesForImages, maxImageSize, ValidationErrorMessages } from '@coorum-research/shared-common/shared/survey/survey-validation'

interface UploadFileCallbackData {
  file: File
  content: string
}

export type FileUploadOptions = {
  onUploadingChange?: (isUploading: boolean) => void
  onValidationError?: (error: string) => void
  onValueChange?: (name: string, value: SurveyImageAnswerValue[]) => void
}

/**
 * ファイルのバリデーション処理
 */
export function validateFile(
  file: File | SurveyImageAnswerValue | null | undefined,
  isRequired: boolean = false,
  isUploading: boolean = false,
): { isValid: boolean, errorMessage?: string } {
  // アップロード中の場合はエラー
  if (isUploading) {
    return {
      isValid: false,
      errorMessage: ValidationErrorMessages.UPLOADING,
    }
  }

  // 必須チェック
  if (isRequired && (!file || (Array.isArray(file) && file.length === 0))) {
    return {
      isValid: false,
      errorMessage: ValidationErrorMessages.REQUIRED,
    }
  }

  // ファイルが存在しない場合は必須チェックのみ
  if (!file) {
    return { isValid: true }
  }

  // File型の場合のみサイズと形式をチェック
  if (file instanceof File) {
    // ファイルサイズのチェック
    if (file.size > maxImageSize) {
      return {
        isValid: false,
        errorMessage: 'ファイルのサイズは10MBを超えてはいけません',
      }
    }

    // 画像タイプのチェック
    if (!acceptedTypesForImages.includes(file.type)) {
      const acceptedExtensions = acceptedTypesForImages.map((v: string) => v.replace('image/', '')).join(', ')
      return {
        isValid: false,
        errorMessage: `ファイルの種類が対応していません。\n対応ファイル: ${acceptedExtensions}`,
      }
    }
  }

  return { isValid: true }
}

/**
 * ファイルアップロードの共通処理
 */
export async function handleFileUpload(
  name: string,
  event: ElementFileUploadEvent,
  options: FileUploadOptions = {},
) {
  const {
    onUploadingChange,
    onValidationError,
    onValueChange,
  } = options

  onUploadingChange?.(true)

  try {
    const originalCallback = event.callback
    event.callback = (results: UploadFileCallbackData[], errors: string[]) => {
      if (errors?.length) {
        console.error('Upload error:', errors)
        onValidationError?.('アップロード中にエラーが発生しました')
        return
      }

      if (results?.length) {
        const uploadedData = results[0]
        if (uploadedData) {
          const formValue = {
            name: uploadedData.file.name,
            type: uploadedData.file.type,
            content: uploadedData.content,
          }
          originalCallback?.(results, errors)
          onValueChange?.(name, [formValue])
        }
      } else {
        originalCallback?.(results, errors)
      }
      onUploadingChange?.(false)
    }

    return event
  } catch (error) {
    console.error('Upload failed:', error)
    onValidationError?.('アップロードに失敗しました')
    onUploadingChange?.(false)
    throw error
  }
}

export class SurveyElementFormHandlerFile implements SurveyElementFormHandler {
  readonly #elementJson: QuestionFileModelData
  #elementResultRecord: SurveyAnswerRecord | null
  #isUploading: boolean = false

  constructor(
    elementJson: QuestionFileModelData,
    elementResultRecord: SurveyAnswerRecord | null,
  ) {
    this.#elementJson = elementJson
    this.#elementResultRecord = elementResultRecord
  }

  get elementJson(): QuestionFileModelData {
    return this.#elementJson
  }

  get surveyModel(): SurveyModel | null {
    return null
  }

  get data(): SurveyAnswerRecord {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    const defaultValue: SurveyImageAnswerValue[] = []
    return this.#elementResultRecord?.[this.#elementJson.name] || defaultValue
  }

  getComment(): string {
    return this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`] as unknown as string || ''
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    const defaultValue: SurveyImageAnswerValue[] = []
    this.#elementResultRecord[this.#elementJson.name] = value || defaultValue
  }

  setElementResultRecordComment(comment: string) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[`${this.#elementJson.name}-Comment`] = comment || ''
  }

  setUploadingState(isUploading: boolean): void {
    this.#isUploading = isUploading
  }

  getUploadingState(): boolean {
    return this.#isUploading
  }

  /**
   * SurveyResultRecordは objectで、validation schema(@see createSurveyElementSchema) は
   * { type, value, comment }なので validation用にdataを変換する
   * 本来は、二つが同じであればいいのだが、zod schemaを合わせられなかった。
   */
  _getElementResultRecordForValidate() {
    const defaultValue: SurveyImageAnswerValue[] = []

    return {
      type: this.#elementJson.type,
      value: this.#elementResultRecord?.[this.#elementJson.name] || defaultValue,
      comment: this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`],
    }
  }

  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    const value = this.getValue() as SurveyImageAnswerValue[]
    const fileData = value.length > 0 ? value[0] : null
    return validateFile(fileData, this.#elementJson.isRequired, this.#isUploading)
  }
}
